import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/cn";
import { buildBlankEvent, cloneFrom, RESERVED_SLUGS } from "@/lib/new-event";

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;

type Template = "blank" | { kind: "copy"; sourceSlug: string };
type EventSummary = { slug: string; title: string; status: "draft" | "published" };

export function AdminNewEvent() {
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [tagline, setTagline] = useState("");
  const [date, setDate] = useState("");
  const [template, setTemplate] = useState<Template>("blank");
  const [existing, setExisting] = useState<EventSummary[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.events.list().then(
      (list) => !cancelled && setExisting(list),
      () => !cancelled && setExisting([]),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  function handleTitleChange(next: string) {
    const prevSuggested = slugify(title);
    setTitle(next);
    if (!slug || slug === prevSuggested) setSlug(slugify(next));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!SLUG_RE.test(slug)) return setError("Slug must be lowercase letters, digits and dashes (2–64).");
    if (RESERVED_SLUGS.has(slug)) return setError(`"${slug}" is reserved. Pick a different slug.`);
    if (!title.trim()) return setError("Title is required.");

    setSubmitting(true);
    try {
      const body =
        template === "blank"
          ? buildBlankEvent({ slug, title: title.trim(), tagline: tagline.trim(), date: date.trim() })
          : cloneFrom(await api.events.get(template.sourceSlug), {
              slug,
              title: title.trim(),
              tagline: tagline.trim(),
              date: date.trim(),
            });
      await api.events.create(body);
      navigate(`/admin/events/${slug}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create event.";
      // Server returns 400 "Event already exists." when the slug was
      // taken between our index fetch and submit; surface it cleanly.
      if (/already exists/i.test(message)) {
        setError("An event with this slug already exists. Pick a different slug.");
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const slugInvalid = slug !== "" && !SLUG_RE.test(slug);
  const slugReserved = slug !== "" && RESERVED_SLUGS.has(slug);
  const slugTaken = (existing ?? []).some((e) => e.slug === slug);
  const canSubmit =
    !submitting && !slugInvalid && !slugReserved && !slugTaken && slug && title;

  return (
    <div className="mx-auto w-full max-w-3xl">
      <header className="mb-8">
        <Link to="/admin" className="text-xs uppercase tracking-[0.18em] text-muted hover:text-accent">
          ← All events
        </Link>
        <h1 className="display-title mt-3 text-[clamp(2rem,4vw,3rem)] leading-tight">Spin up a new event</h1>
        <p className="mt-2 text-sm text-muted">
          The slug becomes the public URL. Start blank or clone an existing event.
        </p>
      </header>

      <form onSubmit={submit} className="surface-card p-6 sm:p-8 grid gap-5">
        <Field label="Event title">
          <input
            type="text"
            required
            placeholder="Imam Ali Celebration 2026"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="form-input"
          />
        </Field>

        <SlugField
          slug={slug}
          onChange={(v) => setSlug(v.toLowerCase())}
          invalid={slugInvalid}
          taken={slugTaken}
          reserved={slugReserved}
        />

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Tagline (optional)">
            <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value)} className="form-input" />
          </Field>
          <Field label="Date (optional)" hint="Free-form text shown in the hero.">
            <input
              type="text"
              placeholder="Saturday, January 3rd · 2026"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="form-input"
            />
          </Field>
        </div>

        <TemplatePicker existing={existing ?? []} value={template} onChange={setTemplate} />

        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}

        <div className="flex items-center justify-end pt-1">
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              "inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-canvas",
              "dark:bg-accent dark:text-accent-on shadow-glow",
              "transition-all hover:bg-ink/90 dark:hover:bg-accent/90 focus-ring",
              "disabled:opacity-60 disabled:cursor-not-allowed",
            )}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
            )}
            Create draft event
          </button>
        </div>
      </form>

      <p className="mt-6 text-center text-xs text-subtle">
        The event starts as a draft. Toggle modules and publish from the editor.
      </p>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="mt-1.5 block text-xs text-subtle">{hint}</span> : null}
    </label>
  );
}

function SlugField({
  slug,
  onChange,
  invalid,
  taken,
  reserved,
}: {
  slug: string;
  onChange: (v: string) => void;
  invalid: boolean;
  taken: boolean;
  reserved: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink">URL slug</span>
      <div className="mt-1.5 flex items-stretch overflow-hidden rounded-xl border border-line/70 bg-surface focus-within:border-accent focus-within:shadow-[0_0_0_4px_rgb(var(--accent)/0.18)]">
        <span className="grid place-items-center px-3 text-xs text-subtle border-r border-line/70 bg-paper">
          mobeen.pages.dev/
        </span>
        <input
          type="text"
          required
          placeholder="imam-ali-2026"
          value={slug}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent px-3 py-3 text-sm text-ink outline-none placeholder:text-subtle"
        />
      </div>
      <span className="mt-1.5 block text-xs text-subtle">
        Lowercase letters, digits, dashes. 2 to 64 characters.
      </span>
      {invalid ? (
        <span className="mt-1 block text-xs text-red-600 dark:text-red-400">
          Slug doesn't match the allowed shape.
        </span>
      ) : null}
      {taken ? (
        <span className="mt-1 block text-xs text-red-600 dark:text-red-400">
          An event with this slug already exists.
        </span>
      ) : null}
      {reserved ? (
        <span className="mt-1 block text-xs text-red-600 dark:text-red-400">
          This slug is reserved for the platform. Pick a different one.
        </span>
      ) : null}
    </label>
  );
}

function TemplatePicker({
  existing,
  value,
  onChange,
}: {
  existing: EventSummary[];
  value: Template;
  onChange: (t: Template) => void;
}) {
  return (
    <fieldset>
      <legend className="block text-sm font-medium text-ink mb-2">Start from</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        <Choice
          active={value === "blank"}
          onClick={() => onChange("blank")}
          title="Blank slate"
          body="Hero enabled, every other module disabled."
        />
        {existing.map((e) => (
          <Choice
            key={e.slug}
            active={typeof value === "object" && value.sourceSlug === e.slug}
            onClick={() => onChange({ kind: "copy", sourceSlug: e.slug })}
            title={`Copy ${e.title}`}
            body="Clone modules, agenda, and config; rename to your slug."
          />
        ))}
      </div>
    </fieldset>
  );
}

function Choice({
  active,
  onClick,
  title,
  body,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  body: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-2xl border p-4 text-left transition-all focus-ring",
        active
          ? "border-accent/60 bg-accent-soft/40 text-accent-ink dark:text-accent shadow-card"
          : "border-line/60 bg-surface hover:border-line",
      )}
    >
      <span className="block text-sm font-medium text-ink">{title}</span>
      <p className="mt-1.5 text-xs text-muted leading-relaxed">{body}</p>
    </button>
  );
}

function slugify(t: string) {
  return t
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}
